import React, { useCallback, useMemo, useRef } from "react";
import { BotLanguage, WebChatHeaderOptions, WebChatState } from "../WebChatContext";
import { DruidCookies, WebChatOptions } from "../WebChatOptions";
import useSagaReducer from 'use-saga-reducer';
import { put, takeLatest,  all, take } from 'redux-saga/effects';
import logger from 'use-reducer-logger';
// import {  ServiceStatus } from '@druidsoft/botframework-directlinejs'; // not sure for now if resolutions in package.json will work
import { mergeChatOptions } from '../utils/mergeChatOptions';
import { setCookie } from "../utils/cookiesUtils";
import { DirectLine, ServiceStatus } from 'botframework-directlinejs';
import { druidLanguageComparison } from "../logics/druidBusinessLogic";
import createEmotion from "@emotion/css/create-instance";
import { randomHash } from "../utils/randomHash";
import { mobileAndTabletcheck } from "../utils/mobileAndTabletCheck";
import stopDictate from 'botframework-webchat-core/lib/actions/stopDictate';
import setSendBox from 'botframework-webchat-core/lib/actions/setSendBox';
import updateIn from 'simple-update-in';

// import { webChatEventHandler } from './../utils/druidWebChatMiddleware';


export type DruidAction = 
    | { type: 'SET_INITIALIZED', payload: boolean }
    | { type: 'SET_LOADED', payload: boolean }
    | { type: 'SET_MINIMIZED', payload: boolean }
    | { type: 'SET_NEWMESSAGE', payload: boolean }
    | { type: 'SET_SIDE', payload: 'left' | 'right' }
    | { type: 'SET_DIRECTLINEOPTIONS', payload: WebChatState["directlineOptions"] }
    | { type: 'SET_SPEECHOPTIONS', payload: Partial<{ speechToken: string, speechRegion: string, isVoiceChannel: boolean }>}
    | { type: 'SET_DIRECTLINEINSTANCE', payload: DirectLine}
    | { type: 'SET_MERGEDCHATOPTIONS', payload: Partial<WebChatOptions> }
    | { type: 'SET_PRISTINE_MERGEDCHATOPTIONS', payload: Partial<WebChatOptions> } // chat options after all merges. does not contain runtime updates, like mergedchatoptions
    | { type: 'SET_BF_WEBCHAT_STORE', payload: WebChatState["store"] }
    | { type: 'SET_HEADEROPTIONS', payload: Partial<WebChatHeaderOptions>}
    | { type: 'SET_BOTLANGUAGES', payload: BotLanguage[]}
    | { type: 'SET_SELECTEDLANGUAGE', payload: string }
    | { type: 'HIDE_CONTAINERELEMENT', payload: boolean }
    | { type: 'HIDE_SUGGESTEDACTIONS', payload: boolean }
    | { type: 'SET_SHOWTYPING', payload: boolean }
    | { type: 'SET_IS_WINDOW_FOCUSED', payload: boolean }
    | { type: 'SET_IS_MOBILE', payload: boolean }
    | { type: 'SET_IS_VOICEINPUT', payload: boolean }
    | { type: 'RESET', payload?: Partial<WebChatOptions>, withAutoExpand?: boolean }
    | { type: 'OPEN_WEBCHAT' }
    | { type: 'CLOSE_WEBCHAT' }
    | { type: 'SERVICESTATUS_CHANGE', payload: ServiceStatus }
    | { type: 'SET_CONVERSATION_TOKEN', payload: string }
    | { type: 'SET_CONTEXTMENU', payload: string[] }
    | { type: 'SET_DRUID_COOKIES', payload: any } // activity
    | { type: 'SET_LOCALE', payload: string } 
    | { type: 'INITIALIZATION_STATUS', payload: 'uninitialized' | 'success' | 'failed' } // activity
    ;

export type DruidContext = [druidWebChatState: WebChatState, druidWebChatDispatchWithMiddleware: (action: DruidAction) => void, getState: () => WebChatState, druidStyleToEmotionObject: (style: any) => any ];

export function useWebChatReducer(initialOptions: WebChatOptions): DruidContext {
    const initialState: WebChatState = useMemo(() => ({
        instanceID: initialOptions.instanceID,
        initialized: false,
        loaded: false,
        minimized: initialOptions.isWidget!,
        newMessage: false,
        containerElementVisible: true,
        side: 'right',
        locale: window?.navigator?.language || 'en-US',
        contextMenuItems: [],
        directlineOptions: null,
        directlineInstance: null,
        speechOptions: {},
        isMobile: mobileAndTabletcheck(),
        isVoiceInput: false,
        mergedChatOptions: initialOptions,
        pristineMergedChatOptions: initialOptions, // chat options after all merges. does not contain runtime updates, like mergedchatoptions
        headerOptions: {
          showChatTitle: true,
          showCloseButton: true,
          showChangeSideButton: true,
          showLanguageSelector: true,
          showIcon: true,
          showAvatar: false,
          showVoiceInputSelector: true,
          showReload: process.env.NODE_ENV === "development",
          showReset: true
        },
        showTyping: false,
        botLanguages: [],
        // isWindowFocused: true,
        store: null,
        bfWebchatProps: null,
        serviceStatus: ServiceStatus.Offline,
        conversationToken: null,
        INITIALIZATION_STATUS: 'uninitialized',
      }), [initialOptions]);
    
      const initializeState = useCallback((newOptionsFromReset?: Partial<WebChatState>) => {
        // MM: reInitializeChat with init call is not equal to type: 'RESET' action. reset keeps previous mergedChatOptions form state.
        if(newOptionsFromReset) {
          initialState.mergedChatOptions = mergeChatOptions(initialState.mergedChatOptions, newOptionsFromReset);
        }
        return initialState;
      }, [initialState]);

      const lastState = useRef(initialState);
      const getState = useCallback(() => lastState.current, [])

      const reduce = (state: WebChatState, action: DruidAction): WebChatState =>  {
        switch (action.type) {
          case 'SET_INITIALIZED':
            return { ...state, initialized: action.payload };
          case 'SET_LOADED':
            return { ...state, loaded: action.payload };
          case 'SET_MINIMIZED':
            return { ...state, minimized: action.payload };
          case 'SET_NEWMESSAGE':
            return { ...state, newMessage: action.payload };
          case 'SET_SIDE':
            return { ...state, side: action.payload };
          case 'SET_DIRECTLINEOPTIONS':
            return { ...state, directlineOptions: {...state.directlineOptions, ...action.payload }};
          case 'SET_SPEECHOPTIONS':
            return { ...state, speechOptions: {...state.speechOptions, ...action.payload }};
          case 'SET_DIRECTLINEINSTANCE':
            return { ...state, directlineInstance: action.payload };
          case 'SET_IS_MOBILE':
            return { ...state, isMobile: action.payload };
          case 'SET_IS_VOICEINPUT': {
            return { ...state, isVoiceInput: action.payload };
          }
          case 'SET_MERGEDCHATOPTIONS':
          {
            return { ...state, mergedChatOptions: mergeChatOptions(state.mergedChatOptions, action.payload)};
          }
          case 'SET_PRISTINE_MERGEDCHATOPTIONS': 
          {
            return { ...state, pristineMergedChatOptions: action.payload };
          }
          case 'SET_SHOWTYPING': {
            return { ...state, showTyping: action.payload };
          }
          case 'SET_BF_WEBCHAT_STORE':
            return { ...state, store: action.payload};
          case 'HIDE_CONTAINERELEMENT':
              return { ...state, containerElementVisible: !action.payload };
          case 'HIDE_SUGGESTEDACTIONS': {
            // const ret = mergeChatOptions(state.mergedChatOptions, { UI: { hideSuggestedActions: action.payload}});
            // return { ...state,  mergedChatOptions: ret};
            const nextState = updateIn(state, ['mergedChatOptions', 'UI', 'hideSuggestedActions'], (hideSuggestedActions) => action.payload);

            return nextState;
          }
          case 'SERVICESTATUS_CHANGE': 
              return { ...state, serviceStatus: action.payload}
          case 'SET_CONVERSATION_TOKEN':
            return { ...state, conversationToken: action.payload };
          case 'SET_CONTEXTMENU': 
            return {
              ...state,
              contextMenuItems: action.payload
            }
          case 'SET_HEADEROPTIONS': 
            return {
              ...state,
              headerOptions: { ...state.headerOptions,...action.payload }
            }
          case 'SET_BOTLANGUAGES':
            return {
              ...state,
              botLanguages: action.payload
            }
          case 'SET_LOCALE': 
            return {
              ...state,
              locale: action.payload
            }
          // case 'SET_IS_WINDOW_FOCUSED': 
          //   return {
          //     ...state,
          //     isWindowFocused: action.payload
          //   }
          /* fetch handlers result*/
          case 'INITIALIZATION_STATUS': {
            return { ...state, INITIALIZATION_STATUS: action.payload }
          }
          case 'RESET':
              {
          //    initialState.instanceID = ;
               return initializeState(action.payload);
              }
          default:
            return state
        }
      }

      const memoizedReducer = useCallback((state: WebChatState, action: DruidAction): WebChatState =>  {
          const ret = reduce(state, action);
          lastState.current = ret;
          return ret;
        
      }, [initializeState]); // <--- if you have vars/deps inside the reducer that changes, they need to go here
   
      const emotionPool = {};
      const druidStyleToEmotionObject = useMemo(() => {
        // Emotion doesn't hash with nonce. We need to provide the pooling mechanism.
        // 1. If 2 instances use different nonce, they should result in different hash;
        // 2. If 2 instances are being mounted, pooling will make sure we render only 1 set of <style> tags, instead of 2.
        const emotion =
          emotionPool[initialState.instanceID] || (emotionPool[initialState.instanceID] = createEmotion({ key: `druid-webchat--css-${randomHash()}`, nonce: initialState.instanceID}));
      
        return style => emotion.css(style);
      }, [initialState.instanceID]);
      function* handleOpenWebchat(action) {
        const currentState = getState();
       
        setCookie(DruidCookies.DruidWebChatIsOpened, true,  { expires: 1, domain: currentState.mergedChatOptions.cookieDomain || "" }, currentState);
        
        yield all([
            put({
                type: 'SET_MINIMIZED',
                payload: false
              }),
              put({
                type: 'SET_NEWMESSAGE',
                payload: false
              }),
              put({
                type: 'HIDE_CONTAINERELEMENT',
                payload: true
              })
        ]);
      };

      function* handleCloseWebchat(action) {
        const currentState = getState();

        setCookie(DruidCookies.DruidWebChatIsOpened, false,  { expires: 1, domain: currentState.mergedChatOptions.cookieDomain || "" }, currentState);

        if(currentState.mergedChatOptions.backgroundTrafficEnabled) {
            yield all([
              put({
                type: 'SET_MINIMIZED',
                payload: true
              }),
              put({
              type: 'SET_NEWMESSAGE',
              payload: false
              }),
              // put({
              //   type: 'SET_INITIALIZED',
              //   payload: false
              //   }),
              // put({
              //   type: 'INITIALIZATION_STATUS',
              //   payload: 'uninitialized'
              //   }),
              put({
                type: 'HIDE_CONTAINERELEMENT',
                payload: false
              })
          ]);
        } else { // if backgroundTrafficEnabled == false, RESET chat. difference from bfv3 -> it used to destroy only webchat connection.
          // we should refactor in bfv4 also (don't use RESET on close)
          // Should we refactor ? RESET is safer. directlineOptions.token needs to be refreshed when collapsed if RESET will not be used
          yield all([
            put({
              type: 'RESET'
            })
          ]);
        }

       

        // yield put({
        //     type: 'SET_MINIMIZED',
        //     payload: true
        //   });
        // yield put({
        //     type: 'SET_NEWMESSAGE',
        //     payload: false
        //   });
      };

      function* handleChangeStatus(action: DruidAction) {
        const { store } = getState();
        if (action.type === 'SERVICESTATUS_CHANGE') {
          if (action.payload === ServiceStatus.Offline) {
            yield put({
              type: 'SET_MERGEDCHATOPTIONS',
              payload: {
                disabled: true
              }
            });

            yield store?.dispatch({
              type: 'WEB_CHAT/SET_NOTIFICATION',
              payload: {
                id: 'servicestatus_offline_notification',
                level: 'error',
                message: 'Offline - Please check your Internet Connection'
              }
            });
          }

          if (action.payload === ServiceStatus.Warning) {
            yield store?.dispatch({
              type: 'WEB_CHAT/SET_NOTIFICATION',
              payload: {
                id: 'servicestatus_warning_notification',
                level: 'warn',
                message: 'Weak Connection'
              }
            });
          }

          if (action.payload === ServiceStatus.Online) {

            yield all([
              put({
                type: 'SET_MERGEDCHATOPTIONS',
                payload: {
                  disabled: false
                }
              }),
                store?.dispatch({
                type: 'WEB_CHAT/DISMISS_NOTIFICATION',
                payload: {
                  id: 'servicestatus_warning_notification'
                }
              }),
                store?.dispatch({
                type: 'WEB_CHAT/DISMISS_NOTIFICATION',
                payload: {
                  id: 'servicestatus_offline_notification'
                }
              })
            ]);

          }
        }
        // druidWebChatDispatch({
        //   type: ''
        // })
        // 'Weak Connection' : 'Offline - Please check your Internet Connection'
      }

      // function* handleLoaded(action) {
      //   yield all([
      //     put({
      //       type: 'SET_MERGEDCHATOPTIONS',
      //       payload: action
      //     }),
      //     put({
      //       type: 'SET_LOADED',
      //       payload: true
      //     })
      //   ]);

      // }
      function* handleChangeDruidCookies(action) {
        const currentState = getState();
        const { mergedChatOptions, minimized} = currentState;
        const activity = action.payload;
        if (!!mergedChatOptions.druidCookieConsentEnabled) {
          setCookie(DruidCookies.DruidCookieConsent, true, { expires: 1, domain: mergedChatOptions.cookieDomain || "" }, currentState);
        }

        setCookie(DruidCookies.DruidWebChatIsOpened, !minimized, { expires: 1, domain: mergedChatOptions.cookieDomain || "" }, currentState); //getWebchatCollapsedState()

        var parsedJSON = JSON.parse(activity.value);

        setCookie(DruidCookies.DruidConversationToken, parsedJSON.DruidConversationToken || currentState.conversationToken, { expires: 1, domain: mergedChatOptions.cookieDomain || "" }, currentState);

        return;
      }

      function* handleMergedChatOptions(action) {
        const { locale } = action.payload;
        // const { mergedChatOptions: {locale: prevLocale = null} } = {} = getState();
        
        if(locale) {
          yield all([
            put({
              type: 'SET_LOCALE',
              payload: locale
            })
          ]);
        }
      }

      function* handleSetSelectedLanguage(action) {
        const name  = action.payload;
        if (!name) return;
        const { botLanguages, mergedChatOptions, directlineInstance }  = getState();
        const selectedLanguage = botLanguages.find(bl => druidLanguageComparison(name, bl.name));
        const currentState = getState();

        if(selectedLanguage) {
          const toSetLanguageName = name || selectedLanguage.name;

          setCookie(DruidCookies.DruidConversationLanguage, toSetLanguageName, { expires: 1, domain: mergedChatOptions.cookieDomain || "" }, currentState);
          directlineInstance?.postActivity({
            from: { id: mergedChatOptions.userId || "" },
            name: 'LanguageChanged',
            type: 'event',
            value: toSetLanguageName
          }).toPromise();
          
          yield all([
            put({
              type: 'SET_BOTLANGUAGES',
              payload: botLanguages.map(bl => ({
                ...bl,
                isSelected:  druidLanguageComparison(selectedLanguage.name, bl.name)
              }))
            }),
            put({
              type: 'SET_LOCALE',
              payload: toSetLanguageName
            })
          ]);
        }
       
      }

      function* handleSetIsVoiceInput(action) {
        const currentState = getState();
        if(action.payload === false) { // stop dictate if change from voice to keyboard and already in listening state
          currentState.store?.dispatch(stopDictate());
          currentState.store?.dispatch(setSendBox(''));
        }
      }

      function* handleResetWithAutoOpen(action) {
        if(action.withAutoExpand) {
          yield take('SET_LOADED');
          yield put(({type: 'OPEN_WEBCHAT'}));
        }
      }

      function* rootSaga () {
        yield all([
            // takeLatest("OPEN_WEBCHAT", handleOpenWebchat),
            takeLatest("OPEN_WEBCHAT", handleOpenWebchat),
            takeLatest("CLOSE_WEBCHAT", handleCloseWebchat),
            takeLatest("SERVICESTATUS_CHANGE", (action) => handleChangeStatus(action)),
            takeLatest("SET_DRUID_COOKIES", (action) => handleChangeDruidCookies(action)),
            takeLatest("SET_MERGEDCHATOPTIONS", (action) => handleMergedChatOptions(action)),
            takeLatest("SET_SELECTEDLANGUAGE", (action) => handleSetSelectedLanguage(action)),
            takeLatest("SET_IS_VOICEINPUT", (action) => handleSetIsVoiceInput(action)),
            takeLatest("RESET", (action) => handleResetWithAutoOpen(action))
            // takeLatest("ON_LOADEDSAGA", (action) => handleLoaded(action))
          ]);

        //   yield  takeLatest("OPEN_WEBCHAT", handleOpenWebchat);

        //   yield [
        //     fork("OPEN_WEBCHAT", handleOpenWebchat), // saga1 can also yield [ fork(actionOne), fork(actionTwo) ]
        //     fork("CLOSE_WEBCHAT", handleCloseWebchat),
        // ];
    }
    
    // logger(reducer)
      const [druidWebChatState, druidWebChatDispatch] = useSagaReducer<any, React.Reducer<WebChatState, DruidAction>, any>(rootSaga, process.env.NODE_ENV === 'development' ? logger(memoizedReducer): memoizedReducer, initialState);
    
      const druidWebChatDispatchWithMiddleware = useCallback((action: DruidAction) => {
        try {
            // webChatEventHandler && webChatEventHandler(action);
            const currentState = getState();
            //handler for .init({}) provided
            initialOptions.webChatEventHandler && initialOptions.webChatEventHandler(action, currentState);

            //handler for loadConfiguration provided
            if(currentState.mergedChatOptions.webChatEventHandler) {
              currentState.mergedChatOptions.webChatEventHandler(action, currentState);
            }
        } catch {

        }
        druidWebChatDispatch(action);
        return;
      }, [druidWebChatDispatch, initialOptions]); 

      return [druidWebChatState, druidWebChatDispatchWithMiddleware, getState, druidStyleToEmotionObject];
}

